import { call, put, takeEvery } from 'redux-saga/effects';
import { handleApiError } from '../networkErrorHandling/actions';

/**
 * Intercepts API errors and dispatches appropriate error handling
 * This saga should be called whenever an API call fails
 */
function* interceptApiErrorSaga(action) {
  try {
    const { error, context, apiCall } = action.payload;

    // Log the error for debugging
    console.error(`API Error in ${context}:`, {
      error,
      apiCall,
      timestamp: new Date().toISOString()
    });

    // Dispatch the error to the network error handling saga
    yield put(handleApiError(error, context));

  } catch (sagaError) {
    console.error('Error in interceptApiErrorSaga:', sagaError);
    
    // Fallback error handling
    yield put(handleApiError(sagaError, 'api-error-interceptor'));
  }
}

/**
 * Wrapper function to handle API calls with error interception
 * @param {Function} apiCall - The API function to call
 * @param {string} context - Context for error reporting
 * @param {Array} args - Arguments to pass to the API call
 */
function* handleApiCallWithErrorInterception(apiCall, context, ...args) {
  try {
    // Make the API call
    const result = yield call(apiCall, ...args);
    return result;
  } catch (error) {
    // Intercept the error
    yield put({
      type: 'INTERCEPT_API_ERROR',
      payload: { error, context, apiCall: apiCall.name || 'unknown' }
    });
    
    // Re-throw the error so calling code can handle it if needed
    throw error;
  }
}

/**
 * Main API error interceptor saga watcher
 */
export function* apiErrorInterceptorSaga() {
  yield takeEvery('INTERCEPT_API_ERROR', interceptApiErrorSaga);
}

// Export for use in other sagas
export { handleApiCallWithErrorInterception };

/**
 * Helper function to create error interceptor for specific API calls
 * @param {string} context - Context for the API call
 * @returns {Function} - Wrapped API call function
 */
export function createApiErrorInterceptor(context) {
  return function* (apiCall, ...args) {
    return yield call(handleApiCallWithErrorInterception, apiCall, context, ...args);
  };
}
